import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { ReleasingChargeRow } from '@/components/payments/releasing-charge-row';
import { ThemedText } from '@/components/themed-text';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { PageLayout } from '@/components/ui/page-layout';
import { SectionCard, StatRow, StatTile } from '@/components/ui/section-card';
import { SkeletonListRow, SkeletonSummaryCard } from '@/components/ui/skeleton';
import { RECEIVABLES_RELEASING } from '@/content/payments/receivables';
import { usePaymentReleasing } from '@/hooks/use-payment-reporting';
import { getApiErrorMessage } from '@/utils/api-error';
import { formatCurrency } from '@/utils/format-currency';

function goBack() {
  if (router.canGoBack()) {
    router.back();
    return;
  }
  router.replace('/(tabs)/(provider)/payments' as never);
}

function errorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

/**
 * "Liberando": cada pagamento que o cliente já fez e que ainda não liberou,
 * na ordem em que libera, com a data de cada um (`GET
 * /payment-account/releasing`, sem o teto de 35 dias da agenda).
 */
export default function ReleasingScreen() {
  const releasing = usePaymentReleasing();

  if (releasing.isLoading) {
    return (
      <PageLayout title={RECEIVABLES_RELEASING.title} onBack={goBack}>
        <SkeletonSummaryCard />
        <SkeletonListRow />
        <SkeletonListRow />
      </PageLayout>
    );
  }

  if (errorStatus(releasing.error) === 403) {
    return (
      <PageLayout title={RECEIVABLES_RELEASING.title} onBack={goBack}>
        <ErrorState
          icon="lock-closed-outline"
          title="Sem acesso"
          message={getApiErrorMessage(
            releasing.error,
            'Você não pode ver os recebimentos desta conta.',
          )}
        />
      </PageLayout>
    );
  }

  if (releasing.error || !releasing.data) {
    return (
      <PageLayout title={RECEIVABLES_RELEASING.title} onBack={goBack}>
        <ErrorState
          title="Não foi possível carregar"
          message={getApiErrorMessage(releasing.error, 'Tente de novo em instantes.')}
          onRetry={() => releasing.refetch()}
        />
      </PageLayout>
    );
  }

  const { charges, totalAmount } = releasing.data;

  return (
    <PageLayout title={RECEIVABLES_RELEASING.title} onBack={goBack}>
      <SectionCard icon="time-outline" title={RECEIVABLES_RELEASING.title}>
        <StatRow>
          <StatTile
            label={RECEIVABLES_RELEASING.totalLabel}
            value={formatCurrency(totalAmount)}
            emphasis
          />
          <StatTile label={RECEIVABLES_RELEASING.countLabel} value={String(charges.length)} />
        </StatRow>
        <ThemedText className="text-xs leading-4 text-neutral-500">
          {RECEIVABLES_RELEASING.hint}
        </ThemedText>
      </SectionCard>

      <SectionCard icon="calendar-outline" title={RECEIVABLES_RELEASING.listTitle}>
        {charges.length === 0 ? (
          <EmptyState
            icon="cash-outline"
            title={RECEIVABLES_RELEASING.emptyTitle}
            description={RECEIVABLES_RELEASING.emptyBody}
          />
        ) : (
          <View>
            {charges.map((charge, index) => (
              <ReleasingChargeRow
                key={charge.id}
                charge={charge}
                showReleaseDate
                isLast={index === charges.length - 1}
              />
            ))}
          </View>
        )}
      </SectionCard>
    </PageLayout>
  );
}
